import React from 'react';
import { Button } from './Button';

export type Column<T> = {
  key: keyof T | string;
  header: string;
  render?: (row: T) => React.ReactNode;
  width?: string | number;
};

type Props<T> = {
  columns: Column<T>[];
  data: T[];
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  rowDetail?: (row: T) => React.ReactNode;
  getRowClass?: (row: T) => string;
};

export function Table<T>({ columns, data, page, pageSize, total, onPageChange, onPageSizeChange, rowDetail, getRowClass }: Props<T>) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="w-full">
      <div className="overflow-auto rounded-md border bg-card">
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 z-10 bg-muted/80 backdrop-blur text-left text-muted-foreground">
            <tr>
              {columns.map(col => (
                <th key={String(col.key)} style={{ width: col.width }} className="px-3 py-2 border-b font-medium">
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => {
              const detail = rowDetail ? rowDetail(row) : null;
              return (
                <React.Fragment key={idx}>
                  <tr className={`transition-colors hover:bg-accent/60 odd:bg-background even:bg-muted/30 ${getRowClass ? getRowClass(row) : ''}`}>
                    {columns.map(col => (
                      <td key={String(col.key)} className="px-3 py-2 border-b align-middle">
                        {col.render ? col.render(row) : (row as any)[col.key as any]}
                      </td>
                    ))}
                  </tr>
                  {detail ? (
                    <tr className="bg-muted/30">
                      <td className="px-3 py-2 border-b" colSpan={columns.length}>
                        {detail}
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
            {data.length === 0 && (
              <tr>
                <td className="px-3 py-6 text-center text-muted-foreground" colSpan={columns.length}>
                  No data
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Page {page} of {totalPages} — {total} items
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPageChange(1)}>
            « First
          </Button>
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
            ‹ Prev
          </Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
            Next ›
          </Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => onPageChange(totalPages)}>
            Last »
          </Button>
          {onPageSizeChange && (
            <select className="ml-2 rounded-md border border-input bg-background px-2 py-1 text-sm" value={pageSize} onChange={e => onPageSizeChange(Number(e.target.value))}>
              {[5, 10, 20, 50].map(s => (
                <option key={s} value={s}>
                  {s} / page
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
    </div>
  );
}

export default Table;
